import { useCallback, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ShieldAlert,
  AlertTriangle,
  Info,
  ListChecks,
  CheckCircle2,
} from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { StatCard } from "./common/StatCard";
import { LeaguePageHeader } from "./common/LeaguePageHeader";
import { LeagueBreadcrumb } from "./common/LeagueBreadcrumb";
import { MetaItem } from "./common/MetaItem";
import { useGetDashboardQuery } from "../api/endpoints/dashboard";
import { useGetAdjudicationQueueQuery } from "../api/endpoints/adjudicationQueue";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import DataTable from "./common/DataTable";
import { getAdjudicationColumns } from "./common/adjudicationColumns";
import { AdjudicationQueueFilters } from "./AdjudicationQueueFilters";
import {
  Clubhouse_LgAdjudicationIssueSeverityV2,
  Clubhouse_LgAdjudicationIssueTypeV2,
} from "../enums";
import type { Clubhouse_LeagueAdjudicationQueueIssueDto } from "../types/adjudicationQueue";
import { LeaguesV2Events } from "../analytics/leaguesV2Events";
import { useTrackLeaguesV2View } from "../analytics/useTrackLeaguesV2View";

// Server-driven page size for the queue list.
const PAGE_SIZE = 10;
// Debounce the search box so typing doesn't fire a request per keystroke.
const SEARCH_DEBOUNCE_MS = 350;

// The dashboard attention cards deep-link with ?filter=<key>; seed the matching
// server-side filter. Keys without a queue equivalent fall through to no filter.
const FILTER_PARAM_MAP: Record<
  string,
  { severities?: string[]; types?: string[] }
> = {
  adjudication: {
    severities: [Clubhouse_LgAdjudicationIssueSeverityV2.Blocking],
  },
  "missing-scorecards": {
    types: [String(Clubhouse_LgAdjudicationIssueTypeV2.MISSING_SCORECARD)],
  },
  "failed-imports": {
    types: [String(Clubhouse_LgAdjudicationIssueTypeV2.IMPORT_VALIDATION)],
  },
};

type SummaryMetric = {
  label: string;
  count: number;
  icon: React.ReactNode;
  iconBg: string;
  iconColor: string;
};

export function AdjudicationQueuePage() {
  const navigate = useNavigate();
  useTrackLeaguesV2View(LeaguesV2Events.VIEW_ADJUDICATION_QUEUE);
  const [searchParams] = useSearchParams();

  // Seed the filters from the dashboard deep-link (?filter=…) on first render.
  const initialFilters =
    FILTER_PARAM_MAP[searchParams.get("filter") ?? ""] ?? {};
  const [leagues, setLeagues] = useState<string[]>([]);
  const [severities, setSeverities] = useState<string[]>(
    initialFilters.severities ?? [],
  );
  const [types, setTypes] = useState<string[]>(initialFilters.types ?? []);
  const [searchInput, setSearchInput] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const search = useDebouncedValue(searchInput, SEARCH_DEBOUNCE_MS);

  // Any filter/search change returns to the first page.
  const withPageReset =
    <T,>(setter: (value: T) => void) =>
    (value: T) => {
      setter(value);
      setPageIndex(0);
    };

  // All filtering and paging is server-side — the query args are the source of
  // truth, and the endpoint returns just the current page.
  const {
    data: queueData,
    isFetching,
    isError,
    refetch,
  } = useGetAdjudicationQueueQuery({
    page: pageIndex + 1,
    pageSize: PAGE_SIZE,
    leagueIds: leagues.map(Number),
    // Severity is a string enum; its filter values are the wire values already.
    severities: severities as Clubhouse_LgAdjudicationIssueSeverityV2[],
    issueTypes: types.map(Number),
    search: search.trim() || undefined,
  });

  // League filter options come from the operator's full league list (the
  // dashboard), not the loaded page — otherwise only leagues on the current
  // page would be filterable.
  const { data: dashboardData } = useGetDashboardQuery();
  const leagueOptions = useMemo(
    () =>
      (dashboardData?.leagues ?? [])
        .map((l) => ({
          value: String(l.leagueId),
          label: l.name ?? `League ${l.leagueId}`,
        }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [dashboardData?.leagues],
  );

  const summary = queueData?.summary;
  const metrics: SummaryMetric[] = [
    {
      label: "Open Issues",
      count: summary?.totalOpenIssues ?? 0,
      icon: <ListChecks className="size-4" />,
      iconBg: "bg-muted",
      iconColor: "text-text-muted",
    },
    {
      label: "Blocking",
      count: summary?.blockingIssues ?? 0,
      icon: <ShieldAlert className="size-4" />,
      iconBg: "bg-destructive/10",
      iconColor: "text-destructive",
    },
    {
      label: "Warnings",
      count: summary?.warnings ?? 0,
      icon: <AlertTriangle className="size-4" />,
      iconBg: "bg-amber-500/10",
      iconColor: "text-amber-500",
    },
    {
      label: "Info",
      count: summary?.infoIssues ?? 0,
      icon: <Info className="size-4" />,
      iconBg: "bg-sky-500/10",
      iconColor: "text-sky-500",
    },
  ];

  const issues = queueData?.issues ?? [];
  const totalCount = queueData?.totalCount ?? 0;
  const hasActiveFilters =
    leagues.length > 0 ||
    severities.length > 0 ||
    types.length > 0 ||
    search.trim().length > 0;

  const onReview = useCallback(
    (issue: Clubhouse_LeagueAdjudicationQueueIssueDto) => {
      if (issue.leagueId == null) return;
      // Route to the round when the issue is round-scoped, else the league hub.
      // Dedicated scorecard/import resolution workflows can hook in here later.
      if (issue.roundId != null) {
        navigate(`/leagues/${issue.leagueId}/rounds/${issue.roundId}`);
      } else {
        navigate(`/leagues/${issue.leagueId}`);
      }
    },
    [navigate],
  );

  const columns = useMemo(() => getAdjudicationColumns(onReview), [onReview]);

  return (
    <div className="@container/control w-full">
      <div className="flex flex-col gap-6 w-full max-w-screen-2xl mx-auto px-4 pb-4 md:px-6 md:pb-6">
        <LeaguePageHeader
          breadcrumb={
            <LeagueBreadcrumb
              crumbs={[
                { label: "Leagues", onClick: () => navigate("/leagues") },
                { label: "Review Center" },
              ]}
            />
          }
          title="Review Center"
          meta={
            <MetaItem>
              Review and resolve league issues requiring attention
            </MetaItem>
          }
        />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {metrics.map((metric) => (
            <StatCard
              key={metric.label}
              icon={metric.icon}
              count={metric.count}
              label={metric.label}
              iconBg={metric.iconBg}
              iconColor={metric.iconColor}
            />
          ))}
        </div>

        {isError ? (
          <Alert
            variant="destructive"
            className="animate-in fade-in-0 slide-in-from-bottom-1 duration-200 motion-reduce:animate-none"
          >
            <AlertTriangle />
            <AlertTitle>Unable to load the queue</AlertTitle>
            <AlertDescription className="flex flex-col items-start gap-2">
              Something went wrong loading open issues. Please try again.
              <Button
                variant="outline"
                size="sm"
                onClick={() => void refetch()}
              >
                Retry
              </Button>
            </AlertDescription>
          </Alert>
        ) : (
          <>
            <AdjudicationQueueFilters
              leagueOptions={leagueOptions}
              leagues={leagues}
              types={types}
              severities={severities}
              search={searchInput}
              onLeaguesChange={withPageReset(setLeagues)}
              onTypesChange={withPageReset(setTypes)}
              onSeveritiesChange={withPageReset(setSeverities)}
              onSearchChange={withPageReset(setSearchInput)}
            />

            <DataTable
              columns={columns}
              data={issues}
              isLoading={isFetching}
              itemNoun={{ singular: "issue" }}
              serverPagination={{
                pageIndex,
                pageSize: PAGE_SIZE,
                totalRows: totalCount,
                onPageChange: setPageIndex,
              }}
              emptyState={
                hasActiveFilters ? (
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <p className="text-sm font-semibold text-text-primary poppins">
                      No matching issues
                    </p>
                    <p className="text-xs text-text-muted mulish">
                      No open issues match the current filters. Try clearing a
                      filter or the search.
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
                    <div className="flex size-10 items-center justify-center rounded-xl bg-green-500/10">
                      <CheckCircle2 className="size-5 text-green-500" />
                    </div>
                    <p className="text-sm font-semibold text-text-primary poppins">
                      All caught up
                    </p>
                    <p className="text-xs text-text-muted mulish">
                      No open issues across your leagues. New issues appear here
                      as they arise.
                    </p>
                  </div>
                )
              }
            />
          </>
        )}
      </div>
    </div>
  );
}
